import React, { useCallback, useEffect, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import ManuscriptHistoryPanel from "components/organisms/manuscript/history/ManuscriptHistoryPanel";
import ManuscriptPdfPanel from "components/organisms/manuscript/pdf/ManuscriptPdfPanel";
import ManuscriptReviewPanel from "components/organisms/manuscript/review/ManuscriptReviewPanel";

const PANEL_CONFIG = {
    pdf: { label: "PDF", icon: "pdf", subtitle: "컴파일된 PDF 원고를 확인합니다." },
    history: { label: "히스토리", icon: "last", subtitle: "이전 시점으로 복원합니다." },
    review: { label: "리뷰", icon: "thread", subtitle: "선택 영역에 코멘트를 남깁니다." },
};

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    background: var(--color-White);
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 14px;
    border-bottom: solid 1px var(--color-Line);
    flex-shrink: 0;
`;

const PanelHeaderText = styled.div`
    display: flex;
    gap: 10px;
    align-items: end;
`;

const PanelTitle = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: var(--color-NormalBlack);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PanelSubtitle = styled.div`
    font-size: 11px;
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PanelBody = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

function ManuscriptRightPanel({
    activePanel,
    latestCompile,
    pdfArtifact,
    logArtifact,
    compileRevisionSeq,
    compileLogRaw,
    compileLogEntries,
    compileLogLoading,
    compileLogLoadError,
    onCompile,
    onLogEntrySelect,
    compileLoading,
    compileDisabled,
    compileHasLintError,
    compileDisabledReason,
    historyItems,
    highlightedRevisionId,
    restoreMutation,
    handleRestoreRevision,
    saveLabel,
    reviewThreads,
    selectedReviewThreadId,
    onSelectReviewThread,
    onReviewThreadMount,
}) {
    const [isLogPanelOpen, setIsLogPanelOpen] = useState(false);

    const compileStatus = latestCompile?.status || "idle";
    const isCompileFailed = compileStatus === "failed";
    const isCompileInProgress = compileLoading || compileStatus === "queued" || compileStatus === "running";
    const parsedLogEntries = Array.isArray(compileLogEntries) ? compileLogEntries : [];
    const hasLogContent =
        !!logArtifact?.file_url || !!compileLogRaw || !!compileLogLoadError || parsedLogEntries.length > 0;
    const compileTooltipMessage = isCompileInProgress
        ? "컴파일 작업이 진행 중입니다"
        : compileHasLintError
          ? "문법 오류가 있습니다. 클릭하면 오류 위치로 이동합니다"
          : compileDisabledReason || "PDF 변환을 시작합니다";

    useEffect(() => {
        if (isCompileFailed && hasLogContent) {
            setIsLogPanelOpen(true);
        }
    }, [hasLogContent, isCompileFailed, latestCompile?.id]);

    useEffect(() => {
        if (isCompileInProgress) {
            setIsLogPanelOpen(false);
        }
    }, [isCompileInProgress]);

    const handleDownloadPdf = useCallback(async () => {
        if (!pdfArtifact?.file_url) return;
        try {
            const response = await fetch(pdfArtifact.file_url);
            if (!response.ok) throw new Error(`PDF download failed: ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const pathName = new URL(pdfArtifact.file_url, window.location.href).pathname;
            link.href = blobUrl;
            link.download = pathName.split("/").pop() || "manuscript.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (_error) {
            window.open(pdfArtifact.file_url, "_blank", "noopener,noreferrer");
        }
    }, [pdfArtifact?.file_url]);

    const config = PANEL_CONFIG[activePanel] || null;

    return (
        <Panel>
            {config ? (
                <PanelHeader>
                    <Icon name={config.icon} size={"16px"} color={"var(--color-Key)"} />
                    <PanelHeaderText>
                        <PanelTitle>{config.label}</PanelTitle>
                        {config.subtitle ? <PanelSubtitle>{config.subtitle}</PanelSubtitle> : null}
                    </PanelHeaderText>
                </PanelHeader>
            ) : null}
            <PanelBody>
                {activePanel === "pdf" ? (
                    <ManuscriptPdfPanel
                        latestCompile={latestCompile}
                        pdfArtifact={pdfArtifact}
                        logArtifact={logArtifact}
                        compileRevisionSeq={compileRevisionSeq}
                        compileLogRaw={compileLogRaw}
                        compileLogEntries={compileLogEntries}
                        compileLogLoading={compileLogLoading}
                        compileLogLoadError={compileLogLoadError}
                        onLogEntrySelect={onLogEntrySelect}
                        onCompile={onCompile}
                        compileDisabled={compileDisabled}
                        compileHasLintError={compileHasLintError}
                        compileTooltipMessage={compileTooltipMessage}
                        isCompileInProgress={isCompileInProgress}
                        isCompileFailed={isCompileFailed}
                        hasLogContent={hasLogContent}
                        onDownloadPdf={handleDownloadPdf}
                        isLogPanelOpen={isLogPanelOpen}
                        onLogPanelOpenChange={setIsLogPanelOpen}
                    />
                ) : null}

                {activePanel === "history" ? (
                    <ManuscriptHistoryPanel
                        historyItems={historyItems}
                        highlightedRevisionId={highlightedRevisionId}
                        restoreMutation={restoreMutation}
                        handleRestoreRevision={handleRestoreRevision}
                        saveLabel={saveLabel}
                    />
                ) : null}

                {activePanel === "review" ? (
                    <ManuscriptReviewPanel
                        reviewThreads={reviewThreads}
                        selectedReviewThreadId={selectedReviewThreadId}
                        onSelectReviewThread={onSelectReviewThread}
                        onReviewThreadMount={onReviewThreadMount}
                    />
                ) : null}
            </PanelBody>
        </Panel>
    );
}

export default ManuscriptRightPanel;
